import { Chart, ChartConfiguration, ChartType, Scale } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { customTooltip, enrollmentLinePlugin, verticalSeparatorPlugin, yScalesCallback, yTitleTopPlugin } from './utilities';
import { PerformanceEvent } from '../../../models/performance-event.model';

Chart.defaults.color = '#000';
Chart.defaults.font.family = "'Roobert ENEL', sans-serif";
Chart.defaults.font.size = 12;
Chart.defaults.font.weight = 400;

export interface ChartConfigOptions {
  sortedDates: string[];
  datasets: any[];
  timeZone: string;
  performanceData: PerformanceEvent[] | null | undefined;
  onBarClick: ((data: any) => void) | null;
  formatTooltipDate: (date: Date) => string;
  formatDateForXAxis: (date: Date) => string;
  locale: string;
}

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    yTitleTop?: {
      display?: boolean;
      text?: string;
      font?: string;
      color?: string;
    };
  }
}

export abstract class PerformanceChartConfig {
  // Prevent instantiation or inheritance of this class
  private constructor() {}

  static getChartConfig(options: ChartConfigOptions, translate: TranslateService): ChartConfiguration {
    const { sortedDates, datasets, performanceData, onBarClick, locale, formatTooltipDate } = options;

    return {
      type: 'bar',
      plugins: [
		// verticalSeparatorPlugin, 
		yTitleTopPlugin, 
    verticalSeparatorPlugin,
		enrollmentLinePlugin],
      data: {
        labels: sortedDates,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 3.5,
        onClick: (event: any, elements: any, chart: any) => {
          // Check if a bar was actually clicked
          if (elements.length > 0) {
            const elementIndex = elements[0].index;
            const datasetIndex = elements[0].datasetIndex;

            chart.tooltip.setActiveElements([], { x: 0, y: 0 });
            chart.update();

            // Retrieve the raw data object attached to that bar
            const rawData = chart.data.datasets[datasetIndex].data[elementIndex];

            if (onBarClick) {
              onBarClick(rawData);
            }
          }
        },
        // Ensure the cursor changes to a pointer to indicate clickability
        onHover: (event: any, chartElement: any) => {
          event.native.target.style.cursor = chartElement.length ? 'pointer' : 'default';
        },
        interaction: {
          mode: 'point',
          intersect: true,
        },
        plugins: {
          legend: {
            display: false,
          },
          yTitleTop: {
            display: true,
            text: 'kW', // The text you want to display
            font: "800 0.75rem 'Roobert ENEL', sans-serif", // Used for measuring width in beforeLayout
            color: '#000', // The color of the text
          },
          tooltip: {
            enabled: false,
            mode: 'index',
            intersect: false,
            filter: (item: any) => !!item.raw?.eventId,
            position: 'nearest',
            external: (context: any) => customTooltip(context, translate, locale, formatTooltipDate),
          },
        },
        scales: {
          x: {
            type: 'category',
            grid: {
              display: false,
            },
            ticks: {
              color: '#000',
              font: {
                size: 11,
                weight: 400,
              },
            },
          },
          y: {
            axis: 'y',
            type: 'linear',
            ticks: {
              callback: (value: any) => yScalesCallback(value),
              color: '#000',
              font: {
                size: 12,
                weight: 400,
              },
            },
            beginAtZero: true,
            grid: {
              display: true,
            },
            border: {},
            title: {},
            position: 'left',
            afterDataLimits(scale: Scale) {
              const chart: any = scale.chart;
              let highestValue = 0;
              // Iterate through all datasets and their data points
              chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                // Chart.js derives its own limits from visible series only, so match that
                // here — otherwise hiding an outlier rescales the bottom but not the top
                if (!chart.isDatasetVisible(datasetIndex)) return;

                dataset.data.forEach((point: any) => {
                  if (!point) return;
                  // search the true highest value between y and enrollment
                  const maxValueInPoint = Math.max(point.y || 0, point.enrollment || 0);
                  if (maxValueInPoint > highestValue) {
                    highestValue = maxValueInPoint;
                  }
                });
              });
              scale.max = highestValue * 1.1;
            },
          },
        },
      },
    };
  }
}

export const CHART_CONSTANTS = {
  COLOR_PALETTE: ['#37A862', '#4178BC', '#FF782D', '#E8384F', '#607D8B'],
  // Together these leave ~64% of each slot as gap. The enrollment marker is drawn exactly
  // as wide as its bar, so this also controls the separation between those markers.
  CATEGORY_PERCENTAGE: 0.6,
  BAR_PERCENTAGE: 0.6,
  ASPECT_RATIO: 3.5,
};
